export type SortDirection = 'asc' | 'desc';

export const getAmountValue = (row: object): number => {
  const values = row as Record<string, unknown>;
  const amountKey = Object.keys(values).find((key) => key.toLowerCase() === 'amount')
    ?? Object.keys(values).find((key) => key.toLowerCase() === 'debitcredit');
  const value = amountKey ? values[amountKey] : undefined;
  const parsed = typeof value === 'number'
    ? value
    : Number(String(value ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const stripped = value.trim().replace(/[$,%\s]/g, '').replace(/,/g, '');
    const parsed = Number(stripped);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }
  return Number.NaN;
};

const toDateValue = (value: unknown): number | null => {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const naturalCompare = (left: string, right: string): number => {
  const leftParts = left.split(/(\d+)/).filter(Boolean);
  const rightParts = right.split(/(\d+)/).filter(Boolean);
  const length = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < length; index += 1) {
    const leftPart = leftParts[index] ?? '';
    const rightPart = rightParts[index] ?? '';

    if (leftPart === rightPart) continue;

    const leftIsNumber = /^\d+$/.test(leftPart);
    const rightIsNumber = /^\d+$/.test(rightPart);

    if (leftIsNumber && rightIsNumber) {
      return Number(leftPart) - Number(rightPart);
    }

    if (leftIsNumber || rightIsNumber) {
      return leftIsNumber ? -1 : 1;
    }

    return leftPart.localeCompare(rightPart, undefined, { numeric: true, sensitivity: 'base' });
  }

  return left.length - right.length;
};

export const compareValues = (left: unknown, right: unknown): number => {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;

  const leftNumber = toNumber(left);
  const rightNumber = toNumber(right);
  if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
    return leftNumber - rightNumber;
  }

  const leftDate = toDateValue(left);
  const rightDate = toDateValue(right);
  if (leftDate !== null && rightDate !== null) {
    return leftDate - rightDate;
  }

  return naturalCompare(String(left).trim(), String(right).trim());
};

export const sortTransactions = <T extends object>(
  rows: T[],
  field: keyof T,
  direction: SortDirection = 'asc',
): T[] => {
  return [...rows].sort((left, right) => {
    const isAmountSort = String(field).toLowerCase() === 'amount';
    const leftValue = isAmountSort ? getAmountValue(left as Record<string, unknown>) : (left as any)[field];
    const rightValue = isAmountSort ? getAmountValue(right as Record<string, unknown>) : (right as any)[field];
    const comparison = compareValues(leftValue, rightValue);
    return direction === 'asc' ? comparison : -comparison;
  });
};
